import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '~/stores/authStore'
import { storeToRefs } from 'pinia'
import type { NuxtApp } from '@nuxt/types/app'
import type { Chat, Message } from '~/types/chat'
import { AppEvents } from '~/types/events'

class SocketUtilsClient {
  private socket: Socket
  private nuxtApp: NuxtApp

  constructor(_nuxtApp, _userId) {
    const config = useRuntimeConfig()
    this.nuxtApp = _nuxtApp

    this.waitForUserId().then((userId) => {
      this.socket = io(`${config.public.WS_BASE_URL}/chat`, {
        reconnection: true,
        extraHeaders: {
          Authorization: userId,
        },
      });

      this.socket.on('connect', () => {
        this.socket.io.opts.extraHeaders['Authorization'] = userId;
        console.log('chat:connected', this.socket);
        this.setupListeners();
      });

      this.setupListeners();
    });
  }

  private async waitForUserId(): Promise<string> {
    const { userId } = storeToRefs(useAuthStore())
    while (!userId.value) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    return userId.value;
  }

  private setupListeners() {
    this.socket.on('chats', (chats: Chat[]) => {
      console.log('Received chats:', chats);
    });

    this.socket.on('newMessage', (message: Message) => {
      console.log('New message:', message);
      (this.nuxtApp as any).$event(AppEvents.FETCH_PREV_MESSAGES);
    });

    this.socket.on('chatException', (response) => {
      this.nuxtApp.$toast?.error(response.message);
      console.log(response);
    });
  }

  async sendMessage({ roomId, receiverId, messageText, fileMimeType = null, fileBuffer = null }): Promise<Message> {
    await this.checkConnection();
    return new Promise(resolve => {
      console.log(roomId, receiverId, messageText)
      this.socket.emit('sendMessage', {
        roomId,
        messageText,
        receiverId,
        fileMimeType,
        fileBuffer
      })
      this.socket.once('newMessage', resolve)
    })
  }
  async getChats(): Promise<Chat[]> {
    await this.checkConnection();
    return new Promise((resolve, reject) => {
      this.socket.emit('getChats', {
        language: (this.nuxtApp as any).$i18n.locale.value,
      })
      this.socket.once('chats', (chats: Chat[]) => {
        resolve(chats)
      })
      this.socket.on('chatException', response => {
        this.nuxtApp.$toast.error(response.message)
        console.log(response)
        reject(response)
      })
    })
  }

  private async checkConnection(): Promise<void> {
    if (!this.socket?.connected) {
      return new Promise((resolve) => {
        this.socket.once('connect', resolve);
        this.socket.connect();
      });
    }
  }

  async getPreviousMessages(roomId: string): Promise<Message[]> {
    await this.checkConnection()

    return new Promise((resolve, reject) => {
      this.socket.emit('getPreviousMessages', {
        roomId: roomId,
      })

      this.socket.once('previousMessages', previousMessages => {
        console.log(previousMessages)
        resolve(previousMessages)
      })
    })
  }

  async getUnreadMessages(): Promise<Chat[]> {
    await this.checkConnection()

    return new Promise(resolve => {
      this.socket.emit('getUnreadMessages')
      this.socket.once('unreadMessages', (response: Chat[]) => {
        resolve(response)
      })
    })
  }

  async readMessages({ roomId }) {
    await this.checkConnection()
    this.socket.emit('readMessages', {
      roomId,
    })
  }

  async startConversation({ oppositeUserId, roomId = null }): Promise<string> {
    await this.checkConnection()

    return new Promise(resolve => {
      this.socket.emit('startConversation', {
        oppositeUserId: oppositeUserId,
      })
      this.socket.once('startConversationRoomId', data => {
        resolve(data?.roomId)
      })
    })
  }

  disconnect() {
    this.socket?.disconnect()
  }

  openChat(chatId: string): void {
    this.socket.emit('openChat', { chatId })
  }
}

export default defineNuxtPlugin(nuxtApp => {
  const userId =  useCookie('userId')
  return {
    provide: {
      socketUtils: new SocketUtilsClient(nuxtApp, userId.value),
    },
  }
})
